import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type WhatsAppConfigDTO } from '../lib/api'
import { qk } from '../lib/query'
import { Icon } from '../components/icons'
import { useToast } from '../components/ui/Toaster'
import { UsersTab } from '../components/config/UsersTab'
import { IntegrationsTab } from '../components/config/IntegrationsTab'
import { AutoReplyTab } from '../components/config/AutoReplyTab'
import { LinksTab } from '../components/config/LinksTab'
import { CatalogTab } from '../components/config/CatalogTab'
import { QuickRepliesTab } from '../components/config/QuickRepliesTab'
import { useAuth } from '../state/AuthContext'

/**
 * Configurações → WhatsApp (QR).
 *
 * The pairing runs inside SAC itself — no external bridge to configure or keep alive. Pairing is
 * still an interactive human act (someone opens WhatsApp on a phone and scans a code that
 * expires in seconds), which is why it lives on a screen rather than in a setup script, and why
 * the QR polls while it is open: a stale code fails to scan and looks like a broken camera.
 */

const STATUS_LABEL: Record<string, string> = {
  connected: 'conectado',
  connecting: 'conectando',
  qr: 'aguardando leitura',
  logged_out: 'desconectado no celular',
  stopped: 'parado',
}

function statusColor(status: string, live: boolean): string {
  if (status === 'connected') return live ? '#12a150' : '#e8a33d'
  if (status === 'logged_out') return '#e5484d'
  if (status === 'qr' || status === 'connecting') return '#e8a33d'
  return '#8b919d'
}

const TABS: [string, string][] = [
  ['whatsapp', 'WhatsApp'],
  ['integracoes', 'Integrações'],
  ['auto', 'Resposta automática'],
  ['respostas', 'Respostas rápidas'],
  ['usuarios', 'Usuários'],
]

export function Config() {
  const { user } = useAuth()
  const qcTop = useQueryClient()
  const isAdmin = user?.role === 'admin'
  // Pedidos de liberação de link: o número aparece na aba, e quem chega com pedido pendente cai
  // direto nela — o pedido existe para ser decidido, não para ser descoberto.
  const { data: linkReqs = [] } = useQuery({ queryKey: ['link-requests'], queryFn: api.linkRequests, enabled: isAdmin })
  const [tab, setTab] = useState<string>(() => (isAdmin && (qcTop.getQueryData<unknown[]>(['link-requests'])?.length ?? 0) > 0 ? 'links' : 'whatsapp'))
  const qc = useQueryClient()
  const toast = useToast()
  const [qrFor, setQrFor] = useState<string | null>(null)
  const [newName, setNewName] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: qk.whatsappConfig(),
    queryFn: api.whatsappConfig,
    // Faster while a QR is on screen: the status flips the instant the phone scans.
    refetchInterval: qrFor ? 3000 : 15000,
  })

  const { data: qr } = useQuery({
    queryKey: qk.whatsappQr(qrFor ?? ''),
    queryFn: () => api.whatsappQr(qrFor!),
    enabled: Boolean(qrFor),
    // WhatsApp rotates the code roughly every 20s; the socket pushes a new one each time.
    refetchInterval: 3000,
  })

  const current = data?.sessions.find((s) => s.id === qrFor)
  useEffect(() => {
    if (qrFor && current?.status === 'connected') {
      setQrFor(null)
      toast('WhatsApp conectado')
      void qc.invalidateQueries({ queryKey: qk.whatsappConfig() })
    }
  }, [qrFor, current?.status, qc, toast])

  const refresh = () => qc.invalidateQueries({ queryKey: qk.whatsappConfig() })

  const create = useMutation({
    mutationFn: () => api.whatsappCreateSession(newName.trim()),
    onSuccess: (s) => {
      setNewName('')
      // Straight to the QR: a session exists to be paired, and making someone hunt for the
      // button after creating one is a step with no decision in it.
      setQrFor(s.id)
      void refresh()
    },
    onError: (e: Error) => toast(e.message, true),
  })

  const link = useMutation({
    mutationFn: (v: { channelAccountId: string; sessionId: string }) => api.whatsappLink(v),
    onSuccess: () => {
      toast('Canal vinculado')
      void refresh()
    },
    onError: (e: Error) => toast(e.message, true),
  })

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'start' | 'stop' | 'logout' | 'delete' }) =>
      action === 'delete'
        ? api.whatsappDeleteSession(id)
        : action === 'logout'
          ? api.whatsappLogout(id)
          : api.whatsappSessionAction(id, action),
    onSuccess: () => void refresh(),
    onError: (e: Error) => toast(e.message, true),
  })

  return (
    <div className="crm">
      <div className="ctx-tabs cfg-tabs">
        {[...TABS, ...(isAdmin ? [['catalogo', 'Catálogo'], ['links', linkReqs.length ? `Links (${linkReqs.length})` : 'Links']] as [string, string][] : [])].map(([k, label]) => (
          <button key={k} className={`ctab2 ${tab === k ? 'on' : ''}`} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'links' && isAdmin ? (
        <LinksTab />
      ) : tab === 'catalogo' && isAdmin ? (
        <CatalogTab />
      ) : tab === 'respostas' ? (
        <QuickRepliesTab />
      ) : tab === 'usuarios' ? (
        <UsersTab />
      ) : tab === 'auto' ? (
        <AutoReplyTab />
      ) : tab === 'integracoes' ? (
        <IntegrationsTab />
      ) : isLoading ? (
        <div className="empty">Carregando…</div>
      ) : (
      <>
      <div className="crm-head">
        <h2>WhatsApp (QR)</h2>
        <p>
          Conecta um número de WhatsApp real lendo um QR code. A conexão roda dentro do próprio
          SAC — não depende de nenhum outro servidor. Diferente da API oficial: não tem janela de
          24h nem templates aprovados, mas quem responde é o próprio número, então trate-o como
          um telefone da equipe e não como um robô.
        </p>
      </div>

      <div className="cfg-block">
        <div className="blk-h">Sessões</div>
        {data?.sessions.length === 0 && (
          <div className="empty" style={{ padding: 18 }}>
            Nenhuma sessão ainda. Crie uma abaixo e leia o QR no celular.
          </div>
        )}

        {data?.sessions.map((s) => {
          const account = data.accounts.find((a) => a.id === s.channelAccountId)
          return (
            <div className="cfg-row" key={s.id}>
              <span className="stat-dot" style={{ background: statusColor(s.status, s.live) }} />
              <div className="cfg-main">
                <div className="cfg-title">
                  {s.name}
                  <span className="cfg-badge">{STATUS_LABEL[s.status] ?? s.status}</span>
                  {/* The row can say "connected" while this process holds no socket — after a
                      restart, before the resume completes. Saying so beats a green dot that lies. */}
                  {s.status === 'connected' && !s.live && <span className="cfg-badge">reconectando</span>}
                </div>
                <div className="cfg-sub">
                  {s.phone ? `+${s.phone}` : 'não pareado'}
                  {s.pushName ? ` · ${s.pushName}` : ''}
                  {account ? ` · ${account.displayName}` : ' · sem canal vinculado'}
                  {s.lastError ? ` · ${s.lastError}` : ''}
                </div>
              </div>

              <div className="cfg-actions">
                {s.status !== 'connected' && (
                  <button className="btn sec sm" onClick={() => setQrFor(s.id)}>
                    Ler QR
                  </button>
                )}
                {!account && (
                  <select
                    className="tk-status"
                    defaultValue=""
                    onChange={(e) =>
                      e.target.value && link.mutate({ channelAccountId: e.target.value, sessionId: s.id })
                    }
                  >
                    <option value="">Vincular a…</option>
                    {data.accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.displayName}
                      </option>
                    ))}
                  </select>
                )}
                {s.live ? (
                  <button className="btn sec sm" onClick={() => act.mutate({ id: s.id, action: 'stop' })}>
                    Parar
                  </button>
                ) : (
                  <button className="btn sec sm" onClick={() => act.mutate({ id: s.id, action: 'start' })}>
                    Iniciar
                  </button>
                )}
                <button
                  className="btn ghost sm"
                  onClick={() => {
                    // Removing unlinks the phone as well; worth one confirmation.
                    if (confirm('Remover esta sessão? O número será desconectado e precisará ler o QR de novo.')) {
                      act.mutate({ id: s.id, action: 'delete' })
                    }
                  }}
                >
                  Remover
                </button>
              </div>
            </div>
          )
        })}

        <div className="cfg-new">
          <input
            placeholder="Nome da nova sessão (ex.: Quartinhos Kids)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && newName.trim() && create.mutate()}
          />
          <button className="btn pri sm" disabled={!newName.trim() || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Criando…' : 'Criar sessão'}
          </button>
        </div>
      </div>

      </>
      )}

      {qrFor && (
        <div className="modal-back" onClick={() => setQrFor(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-h">
              <Icon name="link" size={15} />
              Parear WhatsApp
              <button className="icon-btn" onClick={() => setQrFor(null)}>
                <Icon name="x" size={14} />
              </button>
            </div>
            <div className="modal-b" style={{ alignItems: 'center' }}>
              {qr?.qrCode ? (
                <img src={qr.qrCode} alt="QR code do WhatsApp" width={260} height={260} />
              ) : (
                <p style={{ fontSize: 13, color: 'var(--text-2)', textAlign: 'center' }}>
                  Gerando o código… se a sessão estiver parada, clique em <b>Iniciar</b> primeiro.
                </p>
              )}
              <p style={{ fontSize: 12.5, color: 'var(--text-2)', textAlign: 'center', margin: 0 }}>
                No celular: <b>WhatsApp → Configurações → Aparelhos conectados → Conectar
                aparelho</b>. O código muda a cada poucos segundos; esta tela se atualiza sozinha
                e fecha quando o pareamento terminar.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export type { WhatsAppConfigDTO }
